import { type ApiResponse } from 'apisauce';
import { http } from 'old/services/shared';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { apiClient } from 'common/services';
import deprecatedApiClient from 'common/services/deprecatedApi';
import { type ConsentsLocationState } from './PageConsents';

export const CONSENTS_ERROR_STATUS_CODE = 412;

const PageConsentsGuard = () => {
  const history = useHistory<ConsentsLocationState>();

  const navigateToConsents = useCallback(() => {
    if (history.location.pathname !== '/consents') {
      history.push('/consents', {
        referrer: history.location.pathname + history.location.search,
        originalState: history.location.state,
      });
    }
  }, [history]);

  const handleApisauceResponse = useCallback(
    (response: ApiResponse<unknown>) => {
      if (response.status === CONSENTS_ERROR_STATUS_CODE) {
        navigateToConsents();
      }
    },
    [navigateToConsents],
  );

  const handleAxiosResponseOrError = useCallback(
    (responseOrError: any) => {
      if (
        responseOrError?.status === CONSENTS_ERROR_STATUS_CODE ||
        responseOrError?.response?.status === CONSENTS_ERROR_STATUS_CODE
      ) {
        navigateToConsents();
      }
    },
    [navigateToConsents],
  );

  useEffect(() => {
    if (!http.responseTransforms.includes(handleApisauceResponse)) {
      http.addResponseTransform(handleApisauceResponse);
    }

    const interceptorNumber = apiClient.interceptors.response.use(undefined, (error) => {
      handleAxiosResponseOrError(error);
      throw error;
    });

    const interceptorNumberDeprecated = deprecatedApiClient.interceptors.response.use(
      (response) => {
        handleAxiosResponseOrError(response);
        return response;
      },
      (error) => {
        handleAxiosResponseOrError(error);
        throw error;
      },
    );

    return () => {
      apiClient.interceptors.response.eject(interceptorNumber);
      deprecatedApiClient.interceptors.response.eject(interceptorNumberDeprecated);
    };
  }, [handleAxiosResponseOrError, handleApisauceResponse]);

  return null;
};

export default PageConsentsGuard;
